'use client'

import { useState } from 'react'
import { useIntl } from 'react-intl'
import {
  CheckmarkShieldOutline16,
  AuthenticityDiamond16,
  Van16,
  ElectronicsBadge16,
} from '@vinted/monochrome-icons'

import { EscrowFeesModel, clickEvent } from '@marketplace-web/escrow-pricing/escrow-pricing-data'
import { CurrencyAmountModel, formatCurrencyAmount } from '@marketplace-web/currency/currency-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useSession } from '@marketplace-web/shared/session-data'

import DetailedItemPriceBreakdownModal from '../../DetailedItemPriceBreakdownModal'
import DiscountBadge from '../DiscountBadge'

type Props = {
  escrowFees: EscrowFeesModel
  isSellerBusiness: boolean
  itemUserId: number
  itemTitle: string
  itemPrice: CurrencyAmountModel
  itemPhotoSrc?: string | null
  itemId: number
  isUserLoggedIn: boolean
}

const DiscountBadges = ({
  escrowFees,
  isSellerBusiness,
  itemUserId,
  itemTitle,
  itemPrice,
  itemPhotoSrc,
  itemId,
  isUserLoggedIn,
}: Props) => {
  const [isPriceBreakdownModalOpen, setIsPriceBreakdownModalOpen] = useState(false)
  const { track } = useTracking()
  const translate = useTranslate('item.discount_badges')

  const { user } = useSession()
  const userId = user?.id
  const { locale } = useIntl()

  const userTypeSuffix = isSellerBusiness ? '_pro' : ''
  const { buyerProtection, itemVerification, shipping, electronicsVerification } = escrowFees

  const isItemOwner = userId === itemUserId

  const handleModalOpen = () => {
    setIsPriceBreakdownModalOpen(true)

    track(
      clickEvent({
        screen: 'item',
        target: 'discount_info',
        targetDetails: JSON.stringify({
          item_id: itemId,
        }),
      }),
    )
  }

  const formattedItemPrice = formatCurrencyAmount(itemPrice, locale)

  const getBuyerProtectionFeeText = () =>
    buyerProtection?.isFree
      ? translate(`free_buyer_protection_fee${userTypeSuffix}`)
      : translate(`buyer_protection_fee${userTypeSuffix}`, {
          discount: buyerProtection?.discountPercentage,
        })

  const getItemVerificationFeeText = () =>
    itemVerification?.isFree
      ? translate('free_item_verification_fee')
      : translate('item_verification_fee', {
          discount: itemVerification?.discountPercentage,
        })

  const getElectronicsVerificationFeeText = () =>
    electronicsVerification?.isFree
      ? translate('free_electronics_verification_fee')
      : translate('electronics_verification_fee', {
          discount: electronicsVerification?.discountPercentage,
        })

  const getShippingFeeText = () =>
    translate('postage', {
      discount: shipping?.maxDiscountPercentage,
    })

  const discountBadges = [
    {
      iconType: CheckmarkShieldOutline16,
      isVisible: !!buyerProtection?.discountPercentage,
      content: getBuyerProtectionFeeText(),
      id: 'buyer-protection-fee',
    },
    {
      iconType: AuthenticityDiamond16,
      isVisible: !!itemVerification?.discountPercentage,
      content: getItemVerificationFeeText(),
      id: 'item-verification-fee',
    },
    {
      iconType: ElectronicsBadge16,
      isVisible: !!electronicsVerification?.discountPercentage,
      content: getElectronicsVerificationFeeText(),
      id: 'electronics-verification-fee',
    },
    {
      iconType: Van16,
      isVisible: !!shipping?.maxDiscountPercentage,
      content: getShippingFeeText(),
      id: 'shipping',
    },
  ]

  const itemHasDiscounts = discountBadges.some(({ isVisible }) => isVisible)
  const showDiscountBadges = itemHasDiscounts && !isItemOwner && isUserLoggedIn

  if (!showDiscountBadges) return null

  return (
    <div data-testid="discount-badges-container">
      <button
        type="button"
        data-testid="discount-badge-button"
        className="u-flexbox u-flex-wrap u-gap-small u-padding-vertical-medium"
        onClick={handleModalOpen}
      >
        {discountBadges.map(badge => (
          <DiscountBadge key={badge.id} {...badge} />
        ))}
      </button>
      <DetailedItemPriceBreakdownModal
        isVisible={isPriceBreakdownModalOpen}
        onClose={() => setIsPriceBreakdownModalOpen(false)}
        itemTitle={itemTitle}
        itemPrice={formattedItemPrice}
        itemPhotoSrc={itemPhotoSrc}
        escrowFees={escrowFees}
        isSellerBusiness={isSellerBusiness}
        isItemOwner={isItemOwner}
        itemId={itemId}
      />
    </div>
  )
}

export default DiscountBadges
